import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { ButtonModel } from '@notino/react-styleguide';
import { GetItemDataByIdQuery } from '@notino/shared/definitions/types';

import { productBaseInfoMessages } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/messages';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { PRODUCT_ALTERNATIVES_ELEMENT_ID } from '@lib/constants';
import { AddToCartButton } from '@sharedComponents/AddToCartButton/AddToCartButton';
import { Watchdog } from '@sharedComponents/Watchdog';
import { watchdogMessages } from '@sharedComponents/Watchdog/watchdogMessages';
import { noop } from '@utils/noop';

import {
  AddToCartButtonWrapper,
  AddToCartButtonWrapperFullWidth,
  StyledButton,
  StyledButtonFullWidth,
} from '../styled';

interface IBuyButtonProps {
  buttonElementClassName: string;
  product: GetItemDataByIdQuery['vpProductById'];
  showAddToCartModal: boolean;
  disableRedirect: boolean;
  disabled?: boolean;
  hasRegisteredWatchdog?: boolean;
  onProductAdded: (
    product: GetItemDataByIdQuery['vpProductById']
  ) => void | Promise<void>;
  onProductAddFailed?: (
    message: string,
    product: GetItemDataByIdQuery['vpProductById']
  ) => void | Promise<void>;
  onClosingCartModal?: () => void;
}

export const BuyButton: React.FC<IBuyButtonProps> = ({
  buttonElementClassName,
  product,
  showAddToCartModal,
  disableRedirect,
  onProductAdded,
  onClosingCartModal,
  onProductAddFailed = noop,
  hasRegisteredWatchdog,
  disabled,
}) => {
  const { release_discovery_product_card_2026 } = useFeatureFlags();
  const ButtonComponent = release_discovery_product_card_2026
    ? StyledButtonFullWidth
    : StyledButton;

  if (!product) {
    return null;
  }

  return (
    <>
      {product.canBuy && (
        <AddToCartButton
          disabled={disabled}
          buttonElementClassName={buttonElementClassName}
          buttonWrapper={
            release_discovery_product_card_2026
              ? AddToCartButtonWrapperFullWidth
              : AddToCartButtonWrapper
          }
          buttonStyle={ButtonModel.Styles.secondary}
          buttonSize={ButtonModel.Sizes.small}
          product={{
            id: product.id ?? '',
            masterId: product.masterId ?? 0,
            name: product.name ?? '',
            productCode: product.productCode ?? '',
            attributes: product.attributes ?? undefined,
            variantName: product.annotation ?? '',
          }}
          count={1}
          showAddToCartModal={showAddToCartModal}
          wrapable={true}
          disableRedirect={disableRedirect}
          onProductAdded={() => onProductAdded(product)}
          onProductAddFailed={(message) => onProductAddFailed(message, product)}
          onClosingModal={onClosingCartModal}
        />
      )}

      <Watchdog
        hasRegisteredWatchdog={hasRegisteredWatchdog}
        variant={{
          ...product,
          catalogId: String(product.catalogId),
          productCode: product.productCode ?? '',
          url: product.url ?? '',
        }}
        id="productItemWatchdog"
      >
        {({ watchdogActive }) => (
          <ButtonComponent
            disabled={disabled}
            buttonStyle={ButtonModel.Styles.secondary}
            buttonSize={ButtonModel.Sizes.small}
          >
            <FormattedMessage
              {...(watchdogActive
                ? watchdogMessages.watchdogActive
                : watchdogMessages.watchAvailability)}
            />
          </ButtonComponent>
        )}
      </Watchdog>

      {!product.canBuy && !product.showWatchdog && (
        <ButtonComponent
          disabled={disabled}
          buttonStyle={ButtonModel.Styles.secondary}
          buttonSize={ButtonModel.Sizes.small}
          href={`${product.url ?? ''}#${PRODUCT_ALTERNATIVES_ELEMENT_ID}`}
        >
          <FormattedMessage
            {...productBaseInfoMessages.similarProductsButton}
          />
        </ButtonComponent>
      )}
    </>
  );
};
